import { useEffect, useRef, useState } from "react";

import { audioService } from "@/core/audio/audioService";
import BingoCheckOverlay from "./BingoCheckOverlay";

const LOCAL_EVENT = "ic-preview-bingo-check";

// Mocked "claimed line" used only for the preview. Five representative
// numbers drawn across the 90-cell range so the row looks natural.
const DEFAULT_PREVIEW_NUMBERS = [12, 27, 34, 56, 89];

// Timings (ms) for the auto-run preview.
const INTRO_MS = 1400; // heartbeat starts + card lands
const PER_NUMBER_MS = 1300; // between each confirmation
const OUTRO_MS = 900; // linger after final confirm before dismiss

// Heartbeat cadence — starts calm, tightens as we approach the finish.
const HEARTBEAT_CALM_BPM = 60;
const HEARTBEAT_TENSE_BPM = 72; // 70-75 per design brief

// How many numbers remaining (INCLUDING the current one) triggers the
// tense phase. Design brief: "final one or two numbers".
const TENSE_THRESHOLD = 2;

/**
 * triggerBingoCheckPreview — public helper. Fires an auto-run Bingo
 * Check preview on the current window.
 *
 * Emits a local CustomEvent (this preview is host-facing only — it
 * does not need to sync across windows the way the ball preview does).
 */
export function triggerBingoCheckPreview(numbers) {
  const payload = {
    numbers:
      Array.isArray(numbers) && numbers.length > 0
        ? numbers.slice(0, 8)
        : DEFAULT_PREVIEW_NUMBERS,
    ts: Date.now(),
  };
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(LOCAL_EVENT, { detail: payload }));
  }
}

/**
 * BingoCheckPreviewOverlay — a rehearsal-only overlay that auto-plays
 * a full heartbeat-verification sequence over the current window.
 *
 * Mounted at the App root. Listens for the local preview event and
 * drives the verification without touching the real game state.
 *
 * Sequence:
 *   0.0s        Overlay fades in, heartbeat starts pulsing.
 *   INTRO_MS    First number gets the active red highlight.
 *   +PER_NUM    Number confirmed (green tick + soft chime),
 *               next number becomes active. Heartbeat continues.
 *   ...         Repeat until all numbers verified.
 *   +OUTRO_MS   Heartbeat stops instantly. Overlay dismisses.
 *
 * This is where the "signature moment" lives — every timing here maps
 * to the design brief and matches what the live host flow will emit.
 */
export default function BingoCheckPreviewOverlay() {
  const [state, setState] = useState(null);
  // state: { numbers: number[], verifiedCount: number } | null
  const timersRef = useRef([]);

  const clearTimers = () => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  };

  useEffect(() => {
    const handler = (e) => {
      const numbers = e.detail?.numbers || DEFAULT_PREVIEW_NUMBERS;
      clearTimers();
      audioService.stopHeartbeat();

      // Open overlay with 0 verified at calm heartbeat
      setState({ numbers, verifiedCount: 0, bpm: HEARTBEAT_CALM_BPM });

      // Start heartbeat immediately (calm)
      audioService.startHeartbeat(HEARTBEAT_CALM_BPM);

      // Schedule confirmations
      numbers.forEach((_, idx) => {
        const t = window.setTimeout(
          () => {
            const isFinal = idx === numbers.length - 1;
            const nextVerified = idx + 1;
            const remainingAfter = numbers.length - nextVerified;
            // If the NEXT number to verify is within the last N, tense up.
            const shouldBeTense =
              !isFinal && remainingAfter > 0 && remainingAfter <= TENSE_THRESHOLD;
            const nextBpm = shouldBeTense
              ? HEARTBEAT_TENSE_BPM
              : HEARTBEAT_CALM_BPM;
            setState((s) =>
              s ? { ...s, verifiedCount: nextVerified, bpm: nextBpm } : s,
            );
            audioService.playCheckConfirm(isFinal);
            if (isFinal) {
              // Signature moment: heartbeat stops the instant the
              // final number is confirmed.
              audioService.stopHeartbeat();
              // Auto-dismiss the preview after a brief silence so the
              // "cut to celebration" moment is felt.
              const outro = window.setTimeout(() => {
                setState(null);
              }, OUTRO_MS);
              timersRef.current.push(outro);
            } else if (shouldBeTense) {
              // Live-update the heartbeat cadence.
              audioService.setHeartbeatBpm(HEARTBEAT_TENSE_BPM);
            }
          },
          INTRO_MS + idx * PER_NUMBER_MS,
        );
        timersRef.current.push(t);
      });
    };

    window.addEventListener(LOCAL_EVENT, handler);
    return () => {
      window.removeEventListener(LOCAL_EVENT, handler);
      clearTimers();
      audioService.stopHeartbeat();
    };
  }, []);

  return (
    <BingoCheckOverlay
      active={!!state}
      numbers={state?.numbers || []}
      verifiedCount={state?.verifiedCount || 0}
      heartbeatBpm={state?.bpm || HEARTBEAT_CALM_BPM}
      playerLabel="Preview · Heartbeat Verification"
      testidPrefix="bingo-check-preview"
    />
  );
}
